import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Button, useRecipe} from '@chakra-ui/react3'
import {headerIconButtonRecipe} from './header-icon-button.recipe'

/**
 * Local round icon-button chrome shared by the header's cart/account CTAs
 * and the mobile menu toggle (STOK-17031 Step 10).
 *
 * Built on Chakra's `Button` factory rendered `unstyled` (opting out of the
 * global `buttonRecipe`) plus this module's own recipe, so the 36×36 round
 * shape, hover, and focus-ring styling live only here and never touch the
 * shared button theme.
 *
 * Polymorphic via the standard Chakra `as` prop: render it as a native
 * button (the default, for onClick affordances like the mobile menu toggle)
 * or as a router link (for the cart/account CTAs, which navigate) by passing
 * `as={HeaderIconLink}` from `./header-icon-link` together with `to`. That
 * target, rather than the plain app `Link`, is what keeps a second recipe off
 * this component's DOM node — see its own docs for why.
 *
 * Accepts an accessible name via `aria-label` (or any other aria-* prop,
 * e.g. `aria-expanded`/`aria-controls` for toggle affordances) and renders
 * its `children` (the icon) centered inside the round chrome.
 *
 * @component
 * @param {React.ElementType} [props.as] - Element/component to render as
 *   (e.g. `HeaderIconLink` for a link CTA); defaults to a native button.
 * @param {Object|Array} [props.css] - Extra Chakra `css` merged on top of
 *   this component's own recipe styles (e.g. responsive visibility from a
 *   parent slot recipe).
 * @param {React.ReactNode} props.children - The icon centered inside the button.
 * @returns {JSX.Element}
 */
const HeaderIconButton = forwardRef(({as, css, children, ...rest}, ref) => {
    const recipe = useRecipe({recipe: headerIconButtonRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Button ref={ref} as={as} unstyled css={[styles, css]} {...rest}>
            {children}
        </Button>
    )
})

HeaderIconButton.propTypes = {
    as: PropTypes.elementType,
    css: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    children: PropTypes.node
}

if (process.env.NODE_ENV !== 'production') {
    HeaderIconButton.displayName = 'HeaderIconButton'
}

export default HeaderIconButton
